import React, { useEffect, useState, useMemo } from 'react';
import { FiAward, FiRefreshCw, FiX } from 'react-icons/fi';
import { useFlashcardSession } from '@/hooks/useFlashcardSession';
import { FlashcardStack } from './flashcard-stack';
import { FlashcardProgress } from './flashcard-progress';
import { Loader } from '@/components/custom/loader';
import brain5 from '@/pages/study-guide/study-guide-review/img/brain-5.svg';
import './flashcard-quiz.styles.sass';

export function FlashcardQuiz({ onComplete, onClose, limit = 10 }) {
  const { cards, currentCard, currentIndex, isLoading, isComplete, error, lastFeedback, progress, correctCount, wrongCount, fetchSession, submitAnswer, nextCard, resetSession, getResults } = useFlashcardSession();

  // Próximo card para mostrar na pilha
  const nextCardData = cards[currentIndex + 1];
  const nextFlashcardData = nextCardData?.flashcard || nextCardData || {};
  const nextQuestion = nextFlashcardData.question || '';

  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    fetchSession(limit);
  }, []);

  // Extrai dados do flashcard (ANTES das condições para manter ordem dos hooks)
  const flashcardData = currentCard?.flashcard || currentCard || {};
  const correctAnswer = flashcardData.correct_answer || flashcardData['correct-answer'] || '';
  const alternativesArray = flashcardData.alternatives || [];
  const explanation = flashcardData.explanation || lastFeedback?.explanation || '';

  // useMemo DEVE ser chamado antes de qualquer return condicional
  const alternatives = useMemo(() => {
    if (!correctAnswer) return [];
    return alternativesArray.filter(Boolean);
  }, [correctAnswer, JSON.stringify(alternativesArray)]);

  const handleAnswer = async selectedAnswer => {
    if (!currentCard) return;

    // O ID do flashcard pode estar em diferentes lugares dependendo da estrutura
    const flashcardId = currentCard.flashcard_id || currentCard['flashcard-id'] || currentCard.flashcard?.id || currentCard.id;

    const result = await submitAnswer(flashcardId, selectedAnswer);
    if (result) {
      setShowFeedback(true);
    }
  };

  const handleContinue = () => {
    setShowFeedback(false);
    nextCard();
  };

  const handleRestart = () => {
    resetSession();
    fetchSession(limit);
  };

  const handleFinish = () => {
    if (onComplete) {
      onComplete(getResults());
    }
  };

  if (isLoading && !currentCard && !isComplete) {
    return (
      <div className="flashcard-quiz flashcard-quiz--loading">
        <Loader />
        <p className="flashcard-quiz__loading-text">Carregando flashcards...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flashcard-quiz flashcard-quiz--error">
        <p className="flashcard-quiz__error-text">{error}</p>
        <button
          type="button"
          className="btn btn--primary"
          onClick={handleRestart}
        >
          <FiRefreshCw className="mr-2" />
          Tentar Novamente
        </button>
      </div>
    );
  }

  if (isComplete) {
    const results = getResults();
    const accuracy = results.accuracy.toFixed(0);

    return (
      <div className="flashcard-quiz">
        <div className="flashcard-stack">
          <div className="flashcard-card flashcard-card--results">
            {/* Botão de fechar */}
            {onClose && (
              <button
                type="button"
                className="flashcard-card__close-btn"
                onClick={onClose}
                aria-label="Fechar"
              >
                <FiX />
              </button>
            )}

            <div className="flashcard-results">
              <div className="flashcard-results__icon">
                <FiAward className="flashcard-results__icon-svg" />
              </div>

              <h2 className="flashcard-results__title">Sessão Concluída!</h2>

              <div className="flashcard-results__stats">
                <div className="flashcard-results__stat">
                  <span className="flashcard-results__stat-value">{results.totalCards}</span>
                  <span className="flashcard-results__stat-label">Total</span>
                </div>
                <div className="flashcard-results__stat flashcard-results__stat--correct">
                  <span className="flashcard-results__stat-value">{results.correctCount}</span>
                  <span className="flashcard-results__stat-label">Acertos</span>
                </div>
                <div className="flashcard-results__stat flashcard-results__stat--wrong">
                  <span className="flashcard-results__stat-value">{results.wrongCount}</span>
                  <span className="flashcard-results__stat-label">Erros</span>
                </div>
              </div>

              <div className="flashcard-results__accuracy">
                <div className="flashcard-results__accuracy-bar">
                  <div
                    className="flashcard-results__accuracy-fill"
                    style={{ width: `${accuracy}%` }}
                  />
                </div>
                <span className="flashcard-results__accuracy-text">{accuracy}% de aproveitamento</span>
              </div>

              <div className="flashcard-results__actions">
                <button
                  type="button"
                  className="btn btn--outline"
                  onClick={handleRestart}
                >
                  <FiRefreshCw className="mr-2" />
                  Nova Sessão
                </button>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={handleFinish}
                >
                  Concluir
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentCard) {
    return (
      <div className="flashcard-quiz">
        <div className="flashcard-stack">
          <div className="flashcard-card flashcard-card--empty">
            {/* Botão de fechar */}
            {onClose && (
              <button
                type="button"
                className="flashcard-card__close-btn"
                onClick={onClose}
                aria-label="Fechar"
              >
                <FiX />
              </button>
            )}

            <div className="flashcard-empty">
              <div className="flashcard-empty__icon">
                <img
                  src={brain5}
                  alt="Cérebro"
                  className="flashcard-empty__brain"
                />
              </div>

              <h2 className="flashcard-empty__title">Revisão em dia!</h2>
              <p className="flashcard-empty__text">Nenhum flashcard disponível para revisão. Volte mais tarde para revisar seu conteúdo.</p>

              {onClose && (
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={onClose}
                >
                  Fechar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flashcard-quiz">
      <FlashcardStack
        question={flashcardData.question}
        alternatives={alternatives}
        correctAnswer={correctAnswer}
        explanation={explanation}
        onAnswer={handleAnswer}
        onContinue={handleContinue}
        onClose={onClose}
        isLoading={isLoading}
        questionNumber={progress.current}
        totalCards={progress.total}
        showFeedback={showFeedback}
        isCorrect={lastFeedback?.correct}
        isLastCard={progress.current === progress.total}
        correctCount={correctCount}
        wrongCount={wrongCount}
        nextQuestion={nextQuestion}
      />
    </div>
  );
}

export default FlashcardQuiz;
